import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fmtBRL } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar, Legend } from "recharts";
import { TrendingUp, Users, AlertCircle, Wallet, ArrowDownToLine, ArrowUpFromLine, Award, Gift, BadgeCheck } from "lucide-react";

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-4 rounded-sm border-border" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{label}</p>
          <p className="mt-2 kpi-num text-2xl lg:text-3xl">{value}</p>
        </div>
        {Icon && (
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent || "bg-secondary"}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (user && user.role === "autor") return;
    api.get("/dashboard").then(r => setD(r.data));
  }, [user]);
  if (user && user.role === "autor") return <Navigate to="/portal" replace />;

  if (!d) return <Layout title="Dashboard"><div className="grid grid-cols-4 gap-4">{Array.from({length: 8}).map((_,i)=><Skeleton key={i} className="h-24" />)}</div></Layout>;

  const showFin = can(user, "view_financial");
  const showComm = can(user, "view_commissions");

  return (
    <Layout title="Dashboard" subtitle="Visão geral do negócio">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KPI label="Clientes Ativos" value={d.total_clientes} icon={Users} />
        <KPI label="Inadimplentes" value={d.clientes_inadimplentes} icon={AlertCircle} accent="bg-red-50 text-red-700" />
        {showFin && <KPI label="Recebimentos do Mês" value={fmtBRL(d.recebimentos_mes)} icon={ArrowDownToLine} />}
        {showFin && <KPI label="Recebimentos do Dia" value={fmtBRL(d.recebimentos_dia)} icon={ArrowDownToLine} />}
        {showFin && <KPI label="Vendas do Mês" value={fmtBRL(d.vendas_mes)} icon={TrendingUp} />}
        {showFin && <KPI label="Vendas do Ano" value={fmtBRL(d.vendas_ano)} icon={BadgeCheck} />}
        {showFin && <KPI label="Contas a Receber" value={fmtBRL(d.contas_receber)} icon={Wallet} />}
        {showFin && <KPI label="Contas Vencidas" value={fmtBRL(d.contas_vencidas)} icon={AlertCircle} accent="bg-orange-50 text-orange-700" />}
        {showComm && <KPI label="Comissão a Pagar" value={fmtBRL(d.comissao_pagar)} icon={Award} />}
        {showComm && <KPI label="Bonificação a Pagar" value={fmtBRL(d.bonificacao_pagar)} icon={Gift} />}
        {showFin && d.saldo_caixa != null && <KPI label="Saldo de Caixa" value={fmtBRL(d.saldo_caixa)} icon={Wallet} />}
      </div>

      {showFin && d.chart?.length > 0 && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 lg:col-span-2 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg tracking-tight">Vendas e Recebimentos</h3>
                <p className="text-xs text-muted-foreground">Últimos 12 meses</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={d.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="mes" stroke="#5C5C58" fontSize={11} />
                <YAxis stroke="#5C5C58" fontSize={11} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E5E0", borderRadius: 2, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="vendas" stroke="#8C3A3A" strokeWidth={2} name="Vendas" />
                <Line type="monotone" dataKey="recebimentos" stroke="#2E6559" strokeWidth={2} name="Recebimentos" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4 rounded-sm">
            <h3 className="font-display text-lg tracking-tight mb-1">Ranking de Vendedores</h3>
            <p className="text-xs text-muted-foreground mb-4">Por vendas no mês</p>
            <div className="space-y-2">
              {(d.ranking || []).slice(0, 6).map((s, i) => (
                <div key={s.id} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold w-5 text-muted-foreground">#{i+1}</span>
                    <span className="text-sm font-medium">{s.nome}</span>
                  </div>
                  <span className="text-sm tabular-nums font-semibold">{fmtBRL(s.total_mes)}</span>
                </div>
              ))}
              {(d.ranking || []).length === 0 && <p className="text-xs text-muted-foreground">Sem dados</p>}
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
